import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIAS_SEMANA_OPCIONES = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' }
];

const ConfiguracionGlobalModal = ({ configActual, onClose, onSave }) => {
  const [cuposPorDia, setCuposPorDia] = useState(configActual?.cupos_por_dia || 15);
  const [diasOperativos, setDiasOperativos] = useState(configActual?.dias_operativos || [0, 1, 2, 3, 4]);
  const [guardando, setGuardando] = useState(false);

  const toggleDia = (dia) => {
    if (diasOperativos.includes(dia)) {
      setDiasOperativos(diasOperativos.filter(d => d !== dia));
    } else {
      setDiasOperativos([...diasOperativos, dia].sort());
    }
  };

  const handleGuardar = async () => {
    if (cuposPorDia < 1 || cuposPorDia > 50) {
      toast.error('Los cupos deben estar entre 1 y 50');
      return;
    }

    if (diasOperativos.length === 0) {
      toast.error('Debe seleccionar al menos un día operativo');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/configuracion-global`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cupos_por_dia: cuposPorDia,
          dias_operativos: diasOperativos
        })
      });

      // Verificar el status antes de intentar leer el body
      const isOk = response.ok;
      const statusCode = response.status;
      
      // Intentar leer la respuesta
      let data = {};
      try {
        data = await response.json();
      } catch (jsonError) {
        // Si falla al leer JSON, no importa si el status fue OK
        console.log('No se pudo leer JSON de la respuesta');
      }
      
      if (isOk) {
        toast.success('Configuración global actualizada');
        onSave();
      } else {
        toast.error(data?.detail || `Error al actualizar (${statusCode})`);
      }
    } catch (error) {
      console.error('Error:', error);
      // Solo asumir éxito si es específicamente el error del interceptor Y no hubo error de red
      if (error.message && error.message.includes('Body has already been consumed')) {
        // Esperar un momento y recargar para verificar
        toast.success('Configuración actualizada');
        setTimeout(() => onSave(), 500);
      } else {
        toast.error('Error al guardar configuración');
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-yellow-500">Configuración Global</h2>
            <p className="text-gray-400 text-sm mt-1">
              Esta configuración se aplicará a todos los días por defecto
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Cupos por día */}
          <div>
            <label className="block text-white font-semibold mb-2">
              Cupos por Día (Por Defecto)
            </label>
            <p className="text-gray-400 text-sm mb-3">
              Número máximo de citas que se pueden agendar en un día
            </p>
            <input
              type="number"
              min="1"
              max="50"
              value={cuposPorDia}
              onChange={(e) => setCuposPorDia(parseInt(e.target.value))}
              className="w-full bg-slate-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-lg font-semibold focus:outline-none focus:border-yellow-500"
            />
          </div>

          {/* Días operativos */}
          <div>
            <label className="block text-white font-semibold mb-2">
              Días Operativos de la Semana
            </label>
            <p className="text-gray-400 text-sm mb-3">
              Selecciona los días en los que normalmente hay servicio
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {DIAS_SEMANA_OPCIONES.map((dia) => (
                <button
                  key={dia.value}
                  onClick={() => toggleDia(dia.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    diasOperativos.includes(dia.value)
                      ? 'bg-yellow-500 border-yellow-500 text-black font-semibold'
                      : 'bg-slate-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {dia.label}
                </button>
              ))}
            </div>
          </div>

          {/* Resumen */}
          <div className="bg-slate-800 border border-gray-700 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-2">Resumen de Configuración</h3>
            <ul className="space-y-1 text-sm text-gray-400">
              <li>• <span className="text-white font-semibold">{cuposPorDia}</span> citas por día</li>
              <li>
                • Operativo: <span className="text-white font-semibold">
                  {diasOperativos.length === 7 
                    ? 'Todos los días'
                    : diasOperativos.map(d => DIAS_SEMANA_OPCIONES[d].label).join(', ')}
                </span>
              </li>
              <li className="text-yellow-500 mt-2">
                ⚠️ Los días con configuración específica mantendrán sus valores personalizados
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-gray-700 p-6 flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:bg-gray-800"
            disabled={guardando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            disabled={guardando}
          >
            {guardando ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracionGlobalModal;
